import React, { useCallback, useEffect, useMemo, useState } from "react";
import client, { formatApiError } from "@/lib/api";
import { Search, AlertCircle, Users, TrendingUp } from "lucide-react";

const selectCls = "w-full px-3 py-2.5 rounded-xl border-2 border-[#E5DFC8] focus:outline-none focus:border-hv-teal bg-hv-cream text-sm font-bold text-hv-navy";

export default function PrincipalStudents() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async (query) => {
    try {
      const { data } = await client.get("/principal/students", { params: query ? { q: query } : {} });
      setRows(data.students);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  }, []);

  useEffect(() => { load(""); }, [load]);

  // The backend doesn't attribute a teacher name to each student row (only
  // class/section), so unlike poems this filter bar has no TEACHER dropdown
  // — CLASS and SECTION are derived client-side from what /principal/students
  // already returns.
  const classes = useMemo(() => [...new Set((rows || []).map((s) => s.class_name).filter(Boolean))], [rows]);
  const sections = useMemo(() => [...new Set((rows || []).map((s) => s.section_name).filter(Boolean))], [rows]);

  const visible = (rows || []).filter((s) =>
    (!classFilter || s.class_name === classFilter) &&
    (!sectionFilter || s.section_name === sectionFilter)
  );

  return (
    <div data-testid="principal-students">
      <h2 className="hv-heading text-2xl text-hv-navy">All Students</h2>
      <p className="text-hv-slate text-sm mt-1">Every student in your school — filter by class or section to narrow it down.</p>

      <div className="mt-6 hv-card p-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={16} className="absolute start-3 top-1/2 -translate-y-1/2 text-hv-slate" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") load(q); }}
              placeholder="Search students…"
              data-testid="student-search"
              className="w-full ps-9 pe-3 py-2.5 rounded-full border-2 border-[#E5DFC8] focus:outline-none focus:border-hv-teal bg-white text-sm"
            />
          </div>
          <button onClick={() => load(q)} data-testid="student-search-btn" className="hv-btn-secondary">Search</button>
        </div>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-hv-slate font-bold">Class</label>
            <select value={classFilter} onChange={(e) => setClassFilter(e.target.value)} data-testid="student-filter-class" className={`${selectCls} mt-1`}>
              <option value="">All Classes</option>
              {classes.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-hv-slate font-bold">Section</label>
            <select value={sectionFilter} onChange={(e) => setSectionFilter(e.target.value)} data-testid="student-filter-section" className={`${selectCls} mt-1`}>
              <option value="">All Sections</option>
              {sections.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
      </div>

      {err && <p className="text-hv-coral text-sm mt-3 font-bold">{err}</p>}

      <div className="mt-6 grid md:grid-cols-[minmax(0,20rem)_1fr] gap-4">
        <div className="space-y-2" data-testid="student-list">
          {visible.map((s) => (
            <button key={s.id} onClick={() => setSelected(s)} data-testid={`student-row-${s.id}`}
                    className={`w-full text-start rounded-2xl border-2 p-3.5 flex items-center gap-3 transition ${
                      selected?.id === s.id ? "border-hv-teal bg-hv-teal-tint" : "border-[#E5DFC8] bg-white hover:border-hv-teal/60"}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 font-bold text-white ${
                s.needs_attention ? "bg-hv-coral" : "bg-hv-teal"}`}>
                {(s.name || s.email).slice(0, 1).toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="hv-heading text-hv-navy text-sm truncate">{s.name}</div>
                <div className="text-hv-slate text-xs truncate">{s.class_name || "—"}{s.section_name ? ` · ${s.section_name}` : ""}</div>
              </div>
            </button>
          ))}
          {rows && !visible.length && (
            <div className="hv-card p-7 text-hv-slate inline-flex items-center gap-2">
              <AlertCircle size={18} /> No students found.
            </div>
          )}
          {!rows && <div className="p-6 text-hv-slate">Loading…</div>}
        </div>

        <div className="hv-card p-8" data-testid="student-detail">
          {!selected ? (
            <div className="h-full min-h-[18rem] flex flex-col items-center justify-center text-center">
              <div className="hv-icon-circle w-14 h-14 rounded-full flex items-center justify-center">
                <Users size={24} strokeWidth={2.5} />
              </div>
              <h3 className="hv-heading mt-4 text-lg text-hv-navy">Select a student</h3>
              <p className="text-hv-slate text-sm mt-1.5 max-w-xs">Choose a student from the list to view their progress.</p>
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-full flex items-center justify-center shrink-0 font-bold text-white text-xl ${
                  selected.needs_attention ? "bg-hv-coral" : "bg-hv-teal"}`}>
                  {(selected.name || selected.email).slice(0, 1).toUpperCase()}
                </div>
                <div>
                  <h3 className="hv-heading text-xl text-hv-navy">{selected.name}</h3>
                  <p className="text-hv-slate text-sm">{selected.email}</p>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {selected.class_name && <span className="hv-badge">{selected.class_name}</span>}
                {selected.section_name && <span className="hv-badge">{selected.section_name}</span>}
              </div>
              <div className="mt-6 grid grid-cols-2 gap-3 max-w-sm">
                <div className="rounded-2xl hv-tile-teal p-4">
                  <TrendingUp size={16} strokeWidth={2.5} className="text-hv-navy" />
                  <div className="hv-heading text-hv-navy text-2xl mt-2">{selected.completed}/{selected.unlocked}</div>
                  <div className="text-[11px] uppercase tracking-wider text-hv-slate font-bold mt-1">Stories complete</div>
                </div>
                <div className={`rounded-2xl p-4 ${selected.needs_attention ? "hv-tile-coral" : "hv-tile-gold"}`}>
                  <AlertCircle size={16} strokeWidth={2.5} className="text-hv-navy" />
                  <div className="hv-heading text-hv-navy text-sm mt-2">
                    {selected.needs_attention ? "Needs attention" : "On track"}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
